import { JSX, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SurveySummary from 'src/components/NewRenterInterface/Survey/SurveySummary';
import { type SurveySummarySection } from 'src/api/dataVault/survey';
import axiosInstance from 'src/utils/axios';
import type { RootState } from 'src/redux/rootReducerType';
import type { Certification } from 'src/types/Certification';
import {
  setCertStatus,
  setCertStatusLoading,
} from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceSlice';
import { userService } from 'src/services/User.service';
import { fetchContinueCertification } from 'src/services/CertificationContinue.service';
import { interviewActions } from 'src/redux/slices/Interview/interviewSlice';
import CircularProgress from '@mui/material/CircularProgress';
import { Userpilot } from 'userpilot';

const EMPTY_IDS: number[] = [];

export default function InterviewCompletePage(): JSX.Element {
  const { surveyId } = useParams<{ surveyId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [summarySections, setSummarySections] = useState<
    SurveySummarySection[]
  >([]);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryEditing, setSummaryEditing] = useState(false);
  const [continuing, setContinuing] = useState(false);

  const certificationIds: number[] = useSelector(
    (state: RootState) =>
      state.newRenterInterface?.certificationIds ?? EMPTY_IDS
  );

  const certifications: Certification[] = useSelector(
    (state: RootState) => state.newRenterInterface?.certifications ?? []
  );

  const user = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user
  );

  const parsedId = surveyId ? parseInt(surveyId, 10) : null;

  // The certification this survey belongs to, resolved by the survey id.
  const currentCert = useMemo(() => {
    if (parsedId == null) {
      return null;
    }
    return (
      certifications.find((c) =>
        c.cert_household?.cert_members?.some((m) =>
          m.surveys?.includes(parsedId)
        )
      ) ?? null
    );
  }, [certifications, parsedId]);

  // Only prompt when the user manages this household (can act on behalf of
  // the other members) AND there is at least one other member to prompt for.
  const canManageOtherMembers = useMemo(() => {
    if (!currentCert || user?.id == null) {
      return false;
    }
    const members = currentCert.cert_household?.cert_members ?? [];
    const me = members.find((m) => m.user_id === user.id);
    return (
      me?.is_managing === true &&
      members.some((m) => m.user_id != null && m.user_id !== user.id)
    );
  }, [currentCert, user?.id]);

  useEffect(() => {
    if (!certificationIds.length) {
      return;
    }
    let cancelled = false;
    dispatch(setCertStatusLoading(true));
    userService
      .fetchCertStatus(certificationIds)
      .then((data) => {
        if (!cancelled) {
          dispatch(setCertStatus(data ?? {}));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error(
            'Failed to refresh cert status after interview completion',
            err
          );
        }
      })
      .finally(() => {
        dispatch(setCertStatusLoading(false));
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, certificationIds]);

  const handleLoad = useCallback((v: boolean) => setSummaryLoading(v), []);
  const handleSections = useCallback(
    (v: SurveySummarySection[]) => setSummarySections(v),
    []
  );
  const handleEdit = useCallback((v: boolean) => setSummaryEditing(v), []);

  const isComplete =
    summarySections.length > 0 &&
    summarySections.every((s) => s.status === 'complete');

  const handleConfirm = useCallback(async () => {
    if (!parsedId) {
      return;
    }
    try {
      await axiosInstance.patch(`surveys/${parsedId}/accept-answers/`);
    } catch {
      // swallow
    }
    if (canManageOtherMembers) {
      Userpilot.track('questionnaire_completed');
    }
    navigate('/renter');
  }, [parsedId, navigate, canManageOtherMembers]);

  const handleBackToInterview = useCallback(async () => {
    if (!parsedId || continuing) {
      return;
    }
    setContinuing(true);
    try {
      await fetchContinueCertification(parsedId);
      dispatch(interviewActions.clearAllDeferred());
      dispatch(interviewActions.deferFileUpload());
      navigate(`/renter/interview/${surveyId}`);
    } finally {
      setContinuing(false);
    }
  }, [parsedId, continuing, dispatch, navigate, surveyId]);

  if (!parsedId) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', width: '100%', px: 2, pt: 4 }}>
        <Typography color="text.secondary">No survey loaded.</Typography>
      </Box>
    );
  }

  return (
    <Box
      data-testid="complete-screen"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        maxWidth: 480,
        mx: 'auto',
        width: '100%',
      }}
    >
      <Box
        sx={{
          textAlign: 'center',
          pt: 4,
          pb: 2,
          px: 3,
          flexShrink: 0,
        }}
      >
        {summaryLoading ? (
          <>
            <Skeleton
              variant="circular"
              width={72}
              height={72}
              sx={{ mx: 'auto', mb: 2 }}
            />
            <Skeleton
              variant="text"
              width={200}
              sx={{ fontSize: 22, mx: 'auto', mb: 1 }}
            />
            <Skeleton
              variant="text"
              width={300}
              sx={{ fontSize: 14, mx: 'auto' }}
            />
          </>
        ) : isComplete ? (
          <>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                bgcolor: 'success.lighter',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <CheckIcon sx={{ fontSize: 36, color: 'success.main' }} />
            </Box>
            <Typography
              sx={{ fontSize: 22, fontWeight: 700, color: 'text.primary' }}
            >
              You&apos;re all done!
            </Typography>
            <Typography
              sx={{
                fontSize: 14,
                color: 'text.secondary',
                mt: 0.5,
                lineHeight: 1.4,
              }}
            >
              Take a look at your answers before submitting.
            </Typography>
          </>
        ) : (
          <>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                bgcolor: 'warning.lighter',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <ScheduleIcon sx={{ fontSize: 36, color: 'warning.main' }} />
            </Box>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 600,
                color: 'text.primary',
                mb: 1,
              }}
            >
              Interview in progress
            </Typography>
            <Typography
              sx={{
                fontSize: 14,
                color: 'text.secondary',
                lineHeight: 1.5,
              }}
            >
              Review and confirm the answers you&apos;ve provided so far. You
              can return to the interview anytime to finish the rest.
            </Typography>
          </>
        )}
      </Box>

      <Box sx={{ flex: 1 }}>
        <SurveySummary
          surveyId={parsedId}
          renterActive
          reAnswerBasePath={`/renter/interview/${surveyId}/answer`}
          onLoad={handleLoad}
          onSections={handleSections}
          onEdit={handleEdit}
          key={location.key}
        />
      </Box>

      {!summaryLoading && !summaryEditing && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 2,
            bgcolor: 'background.default',
            px: 2,
            pt: 1.5,
            pb: 3,
            display: 'flex',
            gap: 2,
            flexShrink: 0,
          }}
        >
          <Button
            variant="outlined"
            fullWidth
            disabled={continuing}
            onClick={handleBackToInterview}
            sx={{
              borderRadius: '12px',
              height: 48,
              fontSize: 16,
              fontWeight: 600,
              textTransform: 'none',
            }}
            startIcon={
              continuing ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <ArrowBackIcon />
              )
            }
          >
            <span>Back to interview</span>
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={handleConfirm}
            sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              borderRadius: '12px',
              height: 48,
              fontSize: 16,
              fontWeight: 600,
              textTransform: 'none',

              '&:hover': { bgcolor: 'primary.dark' },
              boxShadow: 'none',
            }}
          >
            Confirm my answers
          </Button>
        </Box>
      )}
    </Box>
  );
}
